import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { IQuestionComponent } from 'app/interfaces/IQuestionComponent'
import { TranslocoService } from '@ngneat/transloco'
import { SurveyViewerService } from 'app/modules/survey/survey-viewer/services/survey-viewer.service'
import { AbstractControl } from '@angular/forms'
import { OpenStatementErrorMatcher } from 'app/error-matchers/OpenStatementErrorMatcher'
import { IImageSelection } from 'app/interfaces/IImageSelection'
import { Image } from 'app/models/questions/Image'
import { MatDialog } from '@angular/material/dialog'
import { ImageModalComponent } from 'app/modules/survey/survey-viewer/image-modal/image-modal.component'
import {BehaviorSubject, Subject, takeUntil} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {IValidationResponse, ValidateCommentsAIService} from "@store/validateCommentsAI.service";
import {InputPersistenceService} from '../../../services/input-persistence.service';

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ImageComponent implements IQuestionComponent, OnInit, OnDestroy {

  @Input() question: Image
  @Input() isSmallScreen: boolean
  @Input() activeLang: string

  questionFormControl: AbstractControl
  errorMatcher: OpenStatementErrorMatcher
  isValidAI: Record<string, Record<string, BehaviorSubject<IValidationResponse>>> = {};
  private ngUnsubscribe = new Subject<void>();
  private imageSelectionSubject = new Subject<{sKey: string, questionFormControl: AbstractControl}>();


  constructor(
    public translocoService: TranslocoService,
    public surveyViewerService: SurveyViewerService,
    private validateAI: ValidateCommentsAIService,
    private dialog: MatDialog,
    private inputPersistence: InputPersistenceService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.errorMatcher = new OpenStatementErrorMatcher(this.question.isOptional)
    this.questionFormControl = this.surveyViewerService.activeSurvey$.value.form.get(this.question.key)
    this.question.rowKeys.forEach(rKey => {
      this.question.statementKeys.forEach(sKey => {
        const commentsControl = this.questionFormControl.get(rKey).get(this.question.commentsControlName).get(sKey);
        if (commentsControl){
          this.surveyViewerService.changeFormUpdateStrategy(commentsControl, 'blur');
          // Restaurer la valeur sauvegardée si elle existe (priorité à la valeur en cours de saisie)
          const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
          if (savedValue) {
            commentsControl.setValue(savedValue, {emitEvent: false});
          }
        }
      })
    });

    // Surveiller les changements du Survey pour restaurer les valeurs quand le formulaire se recharge
    // et déclencher la détection de changement quand les règles sont appliquées
    this.surveyViewerService.activeSurvey$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe(survey => {
      if (survey) {
        // Mettre à jour la référence de question si elle a changé (après application des règles)
        const updatedQuestion = survey.questions[this.question.key];
        if (updatedQuestion && updatedQuestion !== this.question) {
          this.question = updatedQuestion as Image;
        }
        
        // Mettre à jour le questionFormControl si le formulaire a changé
        const newQuestionFormControl = survey.form.get(this.question.key);
        if (newQuestionFormControl && newQuestionFormControl !== this.questionFormControl) {
          this.questionFormControl = newQuestionFormControl;
        }
        
        this.question.rowKeys.forEach(rKey => {
          this.question.statementKeys.forEach(sKey => {
            const newCommentsControl = survey.form
              .get(this.question.key)
              ?.get(rKey)
              ?.get(this.question.commentsControlName)
              ?.get(sKey);
            
            if (newCommentsControl) {
              const currentValue = newCommentsControl.value;
              const savedValue = this.inputPersistence.getValue(this.question.key, sKey, rKey);
              
              if (savedValue && (!currentValue || currentValue === '')) {
                setTimeout(() => {
                  newCommentsControl.setValue(savedValue, {emitEvent: false});
                }, 0);
              }
            }
          });
        });
        
        // Forcer la détection de changement quand le survey est mis à jour (règles appliquées)
        // Cela permet de mettre à jour l'affichage des images quand les règles hideIf/visibleIf changent
        this.cdr.markForCheck();
      }
    });

    // Debounce pour la sélection d'image (évite les bugs de double-clic)
    // Attend 300ms après le dernier clic avant d'exécuter la sélection
    this.imageSelectionSubject.pipe(
      debounceTime(300),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(({sKey, questionFormControl}) => {
      this.question.selectImage(sKey, questionFormControl);
      // Forcer la détection de changement pour mettre à jour les libellés
      // Utiliser setTimeout pour s'assurer que la détection se fait après toutes les modifications
      // Cela garantit que les libellés des images verrouillées sont correctement mis à jour
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    });
  }

  displayLastImageSelectionLabel(imageSelection?: IImageSelection): boolean {
    if (!imageSelection || !imageSelection.selectLast) {
      return false
    }

    return this.question.getSelectedStatementsCount(this.questionFormControl) === this.question.rowKeys.length
  }

  disableImageSelection(sKey, questionForm: AbstractControl): boolean {
    const rowKey = this.question.findRowKeyOfSelectedImage(sKey, questionForm)
    const rowHasNoSelection = !questionForm.get(rowKey)
    return (this.question.getSelectedStatementsCount(questionForm) === this.question.rowKeys.length) && rowHasNoSelection
  }

  displayImageSelectionLabel(imageSelection?: IImageSelection): boolean {
    if (!imageSelection || !imageSelection.selectLabel) {
      return false
    }

    return !(imageSelection.selectLast && this.question.getSelectedStatementsCount(this.questionFormControl) === this.question.rowKeys.length)
  }

  buildImageUrlWithoutWidthParameter(url: string): string {
    return url.split('?w=')[0]
  }

  displayRowLabel(sKey, form): Record<string, string> {
    return !this.question.findRowKeyOfSelectedImage(sKey, form) ? undefined :
      this.question.rows[this.question.findRowKeyOfSelectedImage(sKey, form)].label
  }

  selectImage(sKey, questionFormControl, event: MouseEvent): boolean {
    const paths = event.composedPath() as any[]
    for (const pathsKey in paths) {
      if (paths[pathsKey].dataset && paths[pathsKey].dataset['prevent']) {
        return false
      }
    }
    // Émettre dans le Subject pour appliquer le debounce
    this.imageSelectionSubject.next({sKey, questionFormControl});
    return true
  }

  openImageDialog(imgUrl: string) {
    this.dialog.open(ImageModalComponent, {
      data: imgUrl,
      width: '100%',
      maxHeight: '90vh'
    })
  }

  onBlur(value: string, sKey: string, rowKey: string){
    this.questionFormControl.get(rowKey).get(this.question.commentsControlName).get(sKey).setValue(value)
    // Note: On ne nettoie PAS ici car le formulaire peut se recharger juste après

    this.isValidAI[rowKey] ??= {};
    this.isValidAI[rowKey][sKey] ??= new BehaviorSubject<IValidationResponse>({isValid: true});
    if (!this.question.disableOpenStatementsRealtimeValidation) {
      this.validateAI.validateAnswerWithOpenAI(this.question.question[this.activeLang], value, this.activeLang)
        .then((response) => {
          this.isValidAI[rowKey][sKey].next(response);
          this.updateQuestionAIStatus();
        });
    }
  }

  /**
   * Sauvegarde la valeur en temps réel (appelé à chaque frappe)
   */
  onInput(value: string, sKey: string, rowKey: string){
    this.inputPersistence.saveValue(this.question.key, value, sKey, rowKey);
  }

  private updateQuestionAIStatus() {
    const hasError = Object.values(this.isValidAI).some(row =>
      Object.values(row).some(subject => !subject.value.isValid)
    );
    this.validateAI.updateQuestionAIValidationStatus(this.question.key, !hasError);
  }

  ngOnDestroy(){
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.imageSelectionSubject.complete();
  }

}
